import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Card, CardHeader, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, } from "../../components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import {
  Edit, ArrowDownAZ, ArrowDownZA, Plus, Clock, ChevronLeft, ChevronRight,
  FileText, Info, ChevronUp, ListFilter, Undo, Download, Upload,
  ZoomOut, ZoomIn, Users, ChevronDown, Maximize2, Minimize2, Menu, Search, Circle,
} from "lucide-react";
import { toast } from "react-toastify";
import axios from "axios";
import { Tooltip } from 'react-tooltip';
import TeamManager from "./WPManageTeams";
import OrderCreation from "./WPCreateOrder";
import TeamDayView from "./WPTeamDayView";
import PreOrderList from "./WPPreOrderList";
import AddOrEditUserForm from '../company/UserCreator';

const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, "")}/api`;

// Draggable Work Order Component
const DraggableWorkOrder = ({ order, teamId, date, isSelected, onSelect, onDoubleClick, selectedOrders, workOrders, duplicateDetails }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: "WORK_ORDER",
    item: () => {
      if (isSelected) {
        const selectedOrderIds = Array.from(selectedOrders);
        const ordersToDrag = workOrders
          .filter((o) => selectedOrderIds.includes(o.id))
          .map((o) => ({
            id: o.id,
            fromTeamId: o.team,
            fromDate: new Date(o.planned_date_start),
          }));
        return { orders: ordersToDrag };
      }
      return { orders: [{ id: order.id, fromTeamId: teamId, fromDate: date }] };
    },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }));

  const priorityClass =
    order.priority === "high"
      ? "border-red-500"
      : order.priority === "medium"
        ? "border-yellow-500"
        : "border-steel-300";

  const isDuplicate = duplicateDetails && duplicateDetails.length > 1;

  return (
    <div
      ref={drag}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(order.id);
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
        onDoubleClick(order);
      }}
      title={isDuplicate ? `Duplicate found: ${duplicateDetails.map(d => `Team ${d.team}, ${new Date(d.planned_date_start).toLocaleDateString()}`).join("; ")}` : ""}
      className={`p-1 rounded text-xs border cursor-move ${priorityClass} ${isDragging ? "opacity-50" : ""
        } ${isSelected ? "bg-brand-100 border-brand-400" : ""} ${order.status === "planned"
          ? "bg-blue-50 border-blue-200"
          : order.status === "planned_approved"
            ? "bg-orange-50 border-orange-200"
            : order.status === "signed"
              ? "bg-yellow-50 border-yellow-200"
              : order.status === "approved"
                ? "bg-green-50 border-green-200"
                : order.status === "rejected"
                  ? "bg-red-50 border-red-200"
                  : order.status === "closed"
                    ? "bg-steel-50 border-steel-200"
                    : "bg-white border-steel-300"
        } ${isDuplicate ? "bg-red-100 border-red-400" : ""}`}
    >
      <div className="font-medium text-xs">
        {order.work_permit_number} {isDuplicate && <span className="text-red-600 text-[10px]">(Duplicate)</span>}
      </div>
      {order.scope_of_work && (
        <div className="text-[10px] text-slate-600 truncate">
          Scope: {order.scope_of_work}
        </div>
      )}
      <div className="text-[10px] text-slate-500 truncate">
        {order.turbine_data?.location || order.location || "No location"}
      </div>
    </div>
  );
};



// Droppable Calendar Cell Component
const CalendarCell = ({ entity, day, workOrders, onDayClick, onOrderDoubleClick, fetchCompanyData, selectedOrders, toggleOrderSelection, history, setHistory, getTeamLoad, teams, setTeams, getTeamForUser }) => {
  const [loading, setLoading] = useState(false);
  const [isAvailable, setIsAvailable] = useState(true);
  console.log("entity w CalendarCell", entity)
  const getWeekNumber = (date) => {
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date - firstDayOfYear) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay()) / 7);
  };

  const handleDrop = async (item) => {
    const { orders } = item;
    const targetEntityId = entity.id;
    const targetDate = day;

    try {
      setLoading(true);
      const token = localStorage.getItem("access_token");
      const historyEntries = [];

      const targetYear = targetDate.getFullYear();
      const targetWeek = getWeekNumber(targetDate);
      const team = teams.find(t => t.id === targetEntityId);
      const activePeriods = { ...team.active_periods };

      if (!activePeriods[targetYear]) {
        activePeriods[targetYear] = [];
      }
      if (!activePeriods[targetYear].includes(targetWeek)) {
        activePeriods[targetYear].push(targetWeek);
        activePeriods[targetYear].sort((a, b) => a - b);

        await axios.put(
          `${API_BASE_URL}/teams/${targetEntityId}/`,
          { ...team, active_periods: activePeriods },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        setTeams(prevTeams =>
          prevTeams.map(t =>
            t.id === targetEntityId ? { ...t, active_periods: activePeriods } : t
          )
        );
        toast.info(`Team ${team.name} activated for week ${targetWeek} of ${targetYear}`);
      }

      for (const { id, fromTeamId, fromDate } of orders) {
        if (
          fromTeamId === targetEntityId &&
          fromDate.toDateString() === targetDate.toDateString()
        ) {
          continue;
        }

        const orderResponse = await axios.get(`${API_BASE_URL}/workpermit/${id}/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const order = orderResponse.data;

        const originalStart = new Date(order.planned_date_start);
        const originalEnd = new Date(order.planned_date_end);

        const newStart = new Date(targetDate);
        newStart.setHours(originalStart.getHours(), originalStart.getMinutes(), 0, 0);

        const newEnd = new Date(targetDate);
        newEnd.setHours(originalEnd.getHours(), originalEnd.getMinutes(), 0, 0);

        const updateData = {
          ...order,
          team: targetEntityId,
          planned_date_start: newStart.toISOString(),
          planned_date_end: newEnd.toISOString(),
        };

        await axios.put(`${API_BASE_URL}/workpermit/${id}/`, updateData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        historyEntries.push({
          orderId: id,
          fromTeamId,
          fromDate: fromDate.toISOString(),
          toTeamId: targetEntityId,
          toDate: targetDate.toISOString(),
        });
      }

      if (historyEntries.length > 0) {
        setHistory((prev) => [...prev, ...historyEntries]);
        fetchCompanyData();
        toast.success(`${historyEntries.length} orders moved successfully`);
      }
    } catch (error) {
      console.error("Error updating work orders or team availability:", error);
      toast.error("Error updating work orders or team availability");
    } finally {
      setLoading(false);
    }
  };

  const [{ isOver }, drop] = useDrop(() => ({
    accept: "WORK_ORDER",
    drop: (item) => handleDrop(item),
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  }));

  let dayOrders = workOrders.filter((order) => {
    const orderDate = new Date(order.planned_date_start);
    return (
      orderDate.toDateString() === day.toDateString() &&
      (entity.role ? getTeamForUser(entity.id, teams).some(t => t.id === order.team) : order.team === entity.id) &&
      (order.status === "planned" ||
        order.status === "planned_approved" ||
        order.status === "signed" ||
        order.status === "approved" ||
        order.status === "rejected" ||
        order.status === "closed")
    );
  });
  

  

  const load = getTeamLoad ? getTeamLoad(entity.id, day) : "0.0";

  const getDuplicateDetails = (orderNumber) => {
    return workOrders.filter(o => o.work_permit_number === orderNumber);
  };

  return (
    <div
      ref={drop}
      className={`border-t p-2 min-h-[50px] ${isOver ? "bg-green-50" :
          day.getDay() === 0 ? "bg-red-50" :
            "bg-white"
        } ${loading ? "opacity-50" : ""} ${!isAvailable ? "bg-steel-200" : ""}`}
      onClick={() => onDayClick(entity, day)}
    >
      <div className="text-[10px] text-slate-400">
        Load: {load}h {!isAvailable && <span className="text-red-500 ml-1">(Unavailable)</span>}
      </div>
      {(dayOrders.length > 0) ? (
        <div className="space-y-1">
          {dayOrders.map((order) => {
            const duplicateDetails = getDuplicateDetails(order.work_permit_number);
            return (
              <DraggableWorkOrder
                key={order.id}
                order={order}
                teamId={entity.id}
                date={day}
                isSelected={selectedOrders.has(order.id)}
                onSelect={toggleOrderSelection}
                onDoubleClick={() => onOrderDoubleClick(entity, day, order)}
                selectedOrders={selectedOrders}
                workOrders={workOrders}
                duplicateDetails={duplicateDetails}
              />
            );
          })}
          
        </div>
      ) : (
        <div className="text-[10px] text-slate-400">No orders assigned</div>
      )}
    </div>
  );
};

// TimeEditorModal Component
const TimeEditorModal = ({ isOpen, onClose, order, onSave, workOrders }) => {
  const [startTime, setStartTime] = useState(
    order
      ? new Date(order.planned_date_start)
        .toLocaleTimeString("en-GB", { hour12: false })
        .slice(0, 5)
      : "08:00"
  );
  const [endTime, setEndTime] = useState(
    order
      ? new Date(order.planned_date_end)
        .toLocaleTimeString("en-GB", { hour12: false })
        .slice(0, 5)
      : "16:00"
  );

  const handleSave = async () => {
    try {
      const token = localStorage.getItem("access_token");

      const startDate = new Date(order.planned_date_start);
      const [startHours, startMinutes] = startTime.split(":");
      startDate.setHours(parseInt(startHours), parseInt(startMinutes), 0);

      const endDate = new Date(order.planned_date_end);
      const [endHours, endMinutes] = endTime.split(":");
      endDate.setHours(parseInt(endHours), parseInt(endMinutes), 0);

      const updateData = {
        ...order,
        planned_date_start: startDate.toISOString(),
        planned_date_end: endDate.toISOString(),
        status: "planned",
      };

      const response = await axios.put(
        `${API_BASE_URL}/workpermit/${order.id}/`,
        updateData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        onSave();
        onClose();
        toast.success("Order times saved successfully");
      }
    } catch (error) {
      console.error("Error updating times:", error);
      toast.error("Error updating times");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Edit Order Times</DialogTitle>
          <div className="text-[10px] text-slate-500">
            Order: {order?.work_permit_number}
          </div>
        </DialogHeader>

        <div className="grid gap-2 py-2">
          <div className="grid grid-cols-4 items-center gap-2">
            <label className="text-right text-[10px]">Start Time</label>
            <input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="col-span-3 p-1 border border-steel-300 rounded-xl text-[10px] focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-2">
            <label className="text-right text-[10px]">End Time</label>
            <input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="col-span-3 p-1 border border-steel-300 rounded-xl text-[10px] focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="px-4 py-2 bg-white text-black rounded-full hover:bg-steel-100">
            Cancel
          </Button>
          <Button onClick={handleSave} className="px-4 py-2 bg-brand-500 text-white rounded-full hover:bg-brand-600 shadow-lg shadow-brand-500/20">Save changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// UploadExcelModal Component
const UploadExcelModal = ({ isOpen, onClose, onUploadSuccess, workOrders }) => {
  const [file, setFile] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [companies, setCompanies] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (isOpen) {
      setFile(null);
      setSelectedCompany("");
      setErrorMessage("");
    }
  }, [isOpen]);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const token = localStorage.getItem("access_token");
        const response = await axios.get(`${API_BASE_URL}/company/`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setCompanies(response.data);
      } catch (error) {
        console.error("Error fetching companies:", error);
      }
    };

    fetchCompanies();
  }, []);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.name.match(/\.(xlsx|xls)$/)) {
        setFile(selectedFile);
        setErrorMessage("");
      } else {
        setErrorMessage("Please select a valid Excel file (.xlsx or .xls)");
        setFile(null);
      }
    }
  };

  const handleUpload = async () => {
    if (!file || !selectedCompany) {
      setErrorMessage("Please select a file and a company");
      return;
    }

    try {
      setUploading(true);
      const token = localStorage.getItem("access_token");
      const formData = new FormData();
      formData.append("file", file);
      formData.append("company_to", selectedCompany);

      const response = await axios.post(
        `${API_BASE_URL}/upload-excel/`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 201 || response.status === 207) {
        onUploadSuccess();
        onClose();
        toast.success("Excel file uploaded successfully");
      } else {
        setErrorMessage(response.data.error || "Unknown error occurred");
      }
    } catch (error) {
      console.error("Upload error:", error);
      setErrorMessage("Error uploading file");
      toast.error("Error uploading file");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[350px]">
        <DialogHeader>
          <DialogTitle>Upload Excel File</DialogTitle>
        </DialogHeader>
        <div className="grid gap-2">
          <div>
            <label className="text-sm font-medium">Select Company</label>
            <select
              value={selectedCompany}
              placeholder="Select company..."
              onChange={(e) => setSelectedCompany(e.target.value)}
              className="w-full mt-1 p-2 border border-steel-300 rounded-xl text-xs text-slate-600 focus:outline-none focus:ring-1 focus:ring-brand-500"
            >
              <option value="">Select a company</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id} className="text-xs">
                  {company.name}
                </option>
              ))}
            </select>
          </div>
          <div className="mt-2">
            <label className="text-sm font-medium">Select Excel File</label>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              className="w-full p-2 border border-steel-300 rounded-xl mt-1 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500"
              disabled={uploading}
            />
          </div>
          {errorMessage && (
            <div className="text-red-600 text-[10px]">{errorMessage}</div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={uploading} className="px-4 py-2 bg-white text-black rounded-full hover:bg-steel-100">
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={handleUpload}
            disabled={uploading || !file || !selectedCompany}
            className="px-4 py-2 bg-brand-500 text-white rounded-full hover:bg-brand-600 shadow-lg shadow-brand-500/20 disabled:opacity-50"
          >
            {uploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main WorkPermit Component
const WorkPermit = () => {
  const [calendarView, setCalendarView] = useState("week");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showTimeEditor, setShowTimeEditor] = useState(false);
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - date.getDay());
    return date;
  });
  const [viewType, setViewType] = useState("teams");
  const [zoomLevel, setZoomLevel] = useState(1.0);
  const [sortDirection, setSortDirection] = useState("asc");
  const [teams, setTeams] = useState([]);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [selectedTeamDay, setSelectedTeamDay] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showOrdersList, setShowOrdersList] = useState(false);
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [preselectedDate, setPreselectedDate] = useState(null);
  const [preselectedTeam, setPreselectedTeam] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [history, setHistory] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isUserFormOpen, setIsUserFormOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);

  // Funkcje otwierające modalki:
  const openCreateOrder = () => setShowOrderModal(true);
  const openOrdersList  = () => setShowOrdersList(true);
  const openManageTeams   = () => navigate("/teammanager");
  const openUploadExcel   = () => setShowUploadModal(true);
  const handleExportPlan  = () => exportPlan(); 

  // Globalna obsługa klawiszy
  const handleGlobalKeyDown = useCallback((e) => {
    if (e.ctrlKey) {
      switch (e.key.toLowerCase()) {
        case "o": // Ctrl+O
          e.preventDefault();
          openCreateOrder();
          break;
        case "l": // Ctrl+L
          e.preventDefault();
          openOrdersList();
          break;
        case "m": // Ctrl+M
          e.preventDefault();
          openManageTeams();
          break;
        case "u": // Ctrl+U
          e.preventDefault();
          openUploadExcel();
          break;
        case "p": // Ctrl+P
          e.preventDefault();
          handleExportPlan();
          break;
        default:
          break;
      }
    }
  }, [openCreateOrder, openOrdersList, openManageTeams, openUploadExcel, handleExportPlan]);

  useEffect(() => {
    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown);
  }, [handleGlobalKeyDown]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch((err) => {
        console.error("Błąd podczas wchodzenia w tryb pełnoekranowy:", err);
        toast.error("Nie udało się włączyć trybu pełnoekranowego");
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      }).catch((err) => {
        console.error("Błąd podczas wychodzenia z trybu pełnoekranowego:", err);
        toast.error("Nie udało się wyjść z trybu pełnoekranowego");
      });
    }
  };

  const handleUserSave = () => {
    setIsUserFormOpen(false);
    setEditingUserId(null);
    fetchCompanyData();
  };

  const fetchCompanyData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("access_token");

      if (!token) {
        console.error("No authentication token found");
        toast.error("No authentication token found");
        return;
      }

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      };

      const [ordersResponse, teamsResponse, usersResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/workpermit/`, config),
        axios.get(`${API_BASE_URL}/teams/`, config),
        axios.get(`${API_BASE_URL}/users/`, config),
      ]);

      const orders = ordersResponse.data.map((order) => ({
        ...order,
        priority: order.priority || "low",
      })) || [];
      setWorkOrders(orders);

      const orderNumberCounts = {};
      const duplicateDetails = {};
      orders.forEach((order) => {
        orderNumberCounts[order.work_permit_number] = (orderNumberCounts[order.work_permit_number] || 0) + 1;
        if (!duplicateDetails[order.work_permit_number]) {
          duplicateDetails[order.work_permit_number] = [];
        }
        duplicateDetails[order.work_permit_number].push({
          id: order.id,
          team: order.team,
          planned_date_start: order.planned_date_start,
        });
      });

      const duplicates = Object.entries(orderNumberCounts)
        .filter(([_, count]) => count > 1)
        .map(([number]) => number);
      if (duplicates.length > 0) {
        toast.warn(`Duplicate order numbers detected: ${duplicates.join(", ")}`);
      }

      if (usersResponse.data && Array.isArray(usersResponse.data)) {
        const availableUsers = usersResponse.data.filter(
          (user) => user.is_active && !user.is_company_admin
        );
        setCompanyUsers(availableUsers);
      }

      if (teamsResponse.data && Array.isArray(teamsResponse.data)) {
        const updatedTeams = teamsResponse.data.map((team) => ({
          ...team,
          expanded: false,
        }));
        setTeams(updatedTeams);
      }

      

    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error fetching data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanyData();
  }, []);

  const toggleOrderSelection = (orderId) => {
    setSelectedOrders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  const handleUndo = async () => {
    if (history.length === 0) return;

    const lastActions = history.slice(-history.length);
    try {
      setLoading(true);
      const token = localStorage.getItem("access_token");

      for (const { orderId, fromTeamId, fromDate } of lastActions) {
        const orderResponse = await axios.get(`${API_BASE_URL}/workpermit/${orderId}/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const order = orderResponse.data;

        const newStart = new Date(fromDate);
        const newEnd = new Date(fromDate);
        newEnd.setHours(
          new Date(order.planned_date_end).getHours(),
          new Date(order.planned_date_end).getMinutes(),
          0,
          0
        );

        const updateData = {
          ...order,
          team: fromTeamId,
          planned_date_start: newStart.toISOString(),
          planned_date_end: newEnd.toISOString(),
        };

        await axios.put(`${API_BASE_URL}/workpermit/${orderId}/`, updateData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
      }

      setHistory((prev) => prev.slice(0, -lastActions.length));
      fetchCompanyData();
      toast.success("Last move undone");
    } catch (error) {
      console.error("Error undoing action:", error);
      toast.error("Error undoing action");
    } finally {
      setLoading(false);
    }
  };

  const handleSavePlanning = async () => {
    try {
      const token = localStorage.getItem("access_token");

      const plannedOrders = workOrders.filter(
        (order) => order.status === "planned"
      );

      if (plannedOrders.length === 0) {
        toast.warn("No planned orders to approve");
        return;
      }

      const updatePromises = plannedOrders.map((order) => {
        return axios.put(
          `${API_BASE_URL}/workpermit/${order.id}/`,
          {
            ...order,
            status: "planned_approved",
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
      });

      setLoading(true);
      await Promise.all(updatePromises);
      await fetchCompanyData();

      toast.success("Planning saved successfully");
    } catch (error) {
      console.error("Error saving planning:", error);
      toast.error("Error saving planning");
    } finally {
      setLoading(false);
    }
  };

  const navigateWeek = (direction) => {
    setCurrentWeekStart((prevDate) => {
      const newDate = new Date(prevDate);
      let daysToAdd;

      switch (calendarView) {
        case "biweek":
          daysToAdd = 14;
          break;
        case "month":
          daysToAdd = 28;
          break;
        case "3months":
          daysToAdd = 90; // Approximately 3 months
          break;
        case "6months":
          daysToAdd = 180; // Approximately 6 months
          break;
        default:
          daysToAdd = 7; // Week view
      }

      if (direction === "next") {
        newDate.setDate(prevDate.getDate() + daysToAdd);
      } else {
        newDate.setDate(prevDate.getDate() - daysToAdd);
      }
      return newDate;
    });
  };

  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + 0.25, 2.0));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - 0.25, 0.5));
  };

  const getDaysForView = () => {
    const days = [];
    let daysToShow;

    switch (calendarView) {
      case "biweek":
        daysToShow = 14;
        break;
      case "month":
        daysToShow = 28;
        break;
      case "3months":
        daysToShow = 90; // 3 months
        break;
      case "6months":
        daysToShow = 180; // 6 months
        break;
      default:
        daysToShow = 7; // Week view
    }

    for (let i = 0; i < daysToShow; i++) {
      const day = new Date(currentWeekStart);
      day.setDate(currentWeekStart.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const toggleTeamExpansion = (teamId) => {
    setTeams(
      teams.map((team) =>
        team.id === teamId ? { ...team, expanded: !team.expanded } : team
      )
    );
  };

  const handleDayClick = (entity, date) => {
    const dayOrders = workOrders.filter((order) => {
      const orderDate = new Date(order.planned_date_start);
      return (
        orderDate.toDateString() === date.toDateString() &&
        (entity.role ? getTeamForUser(entity.id, teams).some(t => t.id === order.team) : order.team === entity.id)
      );
    });



    setSelectedTeamDay({
      entity,
      date,
      dayOrders,
      viewType: entity.role ? "people" : "teams",
    });
  };

  const handleOrderDoubleClick = (entity, date, order) => {
    const dayOrders = workOrders.filter((order) => {
      const orderDate = new Date(order.planned_date_start);
      return (
        orderDate.toDateString() === date.toDateString() &&
        (entity.role ? getTeamForUser(entity.id, teams).some(t => t.id === order.team) : order.team === entity.id)
      );
    });



    setSelectedTeamDay({
      entity,
      date,
      dayOrders,
      viewType: entity.role ? "people" : "teams",
    });
  };

  const getWeekNumber = (date) => {
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date - firstDayOfYear) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay()) / 7);
  };

  const getDisplayedWeekNumbers = () => {
    const days = getDaysForView();
    const weekNumbers = new Set();

    days.forEach((day) => {
      const weekNumber = getWeekNumber(day);
      weekNumbers.add(`${day.getFullYear()}-${weekNumber}`);
    });

    return Array.from(weekNumbers);
  };

  // Helper function to check if order matches search term
  const orderMatchesSearch = (order, term) => {
    if (!term) return true;
    const lowerTerm = term.toLowerCase();

    // Search in work_permit_number
    if (order.work_permit_number?.toLowerCase().includes(lowerTerm)) return true;

    // Search in order_number
    if (order.order_number?.toLowerCase().includes(lowerTerm)) return true;

    // Search in company_to_name
    if (order.company_to_name?.toLowerCase().includes(lowerTerm)) return true;

    // Search in turbine_data.location
    if (order.turbine_data?.location?.toLowerCase().includes(lowerTerm)) return true;

    // Search in turbine_data.wind_farm_data.location
    if (order.turbine_data?.wind_farm_data?.location?.toLowerCase().includes(lowerTerm)) return true;

    // Search in turbine_data.wind_farm_data.name
    if (order.turbine_data?.wind_farm_data?.name?.toLowerCase().includes(lowerTerm)) return true;

    return false;
  };

  const getActiveEntities = () => {
    if (viewType === "teams") {
      const displayedWeekNumbers = getDisplayedWeekNumbers();
      return teams.filter((team) => {
        const activePeriods = team.active_periods || {};
        const isActive = displayedWeekNumbers.some((week) => {
          const [year, weekNumber] = week.split("-");
          return (
            activePeriods[year] &&
            activePeriods[year].includes(parseInt(weekNumber))
          );
        });

        if (!isActive) return false;
        if (!searchTerm) return true;

        // Check if team name matches search
        if (team.name?.toLowerCase().includes(searchTerm.toLowerCase())) return true;

        // Check if team has any orders matching search
        const teamHasMatchingOrders = workOrders.some(
          (order) => order.team === team.id && orderMatchesSearch(order, searchTerm)
        );

        return teamHasMatchingOrders;
      });
    } else {
      const sortedUsers = companyUsers
        .filter((user) => user.is_active && !user.is_company_admin)
        .map((user) => ({
          id: user.id,
          name: ` ${user.last_name} ${user.first_name}`,
          role: getUserRole(user, teams),
        }))
        .filter((user) => {
          if (!searchTerm) return true;

          // Check if user name matches search
          if (user.name?.toLowerCase().includes(searchTerm.toLowerCase())) return true;

          // Check if user has any orders matching search (through their teams)
          const userTeams = getTeamForUser(user.id, teams);
          const userHasMatchingOrders = workOrders.some((order) =>
            userTeams.some((team) => team.id === order.team) && orderMatchesSearch(order, searchTerm)
          );

          return userHasMatchingOrders;
        })
        .sort((a, b) =>
          sortDirection === "asc"
            ? a.name.localeCompare(b.name)
            : b.name.localeCompare(b.name)
        );
      return sortedUsers;
    }
  };

  const getUserRole = (user, teams) => {
    for (const team of teams) {
      if (team.team_leader === user.id) return "Supervisor";
      if (team.person_responsible_for_preparation_of_work_place === user.id) return "Preparer";
      if (team.team?.includes(user.id)) return "Member";
    }
    return "Unknown";
  };

  const getTeamForUser = (userId, teams) => {
    return teams.filter((team) =>
      [team.team_leader, team.person_responsible_for_preparation_of_work_place, ...team.team].includes(userId)
    );
  };

  const handleCreateOrderFromCalendar = (date, entityId) => {
    const suggestedSlot = suggestSlot(entityId, 8);
    setPreselectedDate(suggestedSlot);
    setPreselectedTeam(entityId);
    setSelectedTeamDay(null);
    setShowOrderModal(true);
  };

  const formatDateRange = (startDate) => {
    const endDate = new Date(startDate);
    let daysToAdd;

    switch (calendarView) {
      case "biweek":
        daysToAdd = 13;
        break;
      case "month":
        daysToAdd = 27;
        break;
      case "3months":
        daysToAdd = 89;
        break;
      case "6months":
        daysToAdd = 179;
        break;
      default:
        daysToAdd = 6;
    }

    endDate.setDate(startDate.getDate() + daysToAdd);

    const startMonth = startDate.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
    const endMonth = endDate.toLocaleDateString("en-GB", { month: "long", year: "numeric" });

    return `${startDate.toLocaleDateString("en-GB", { day: "numeric" })} ${startMonth} - ${endDate.toLocaleDateString("en-GB", { day: "numeric" })} ${endMonth}`;
  };

  const suggestSlot = (entityId, orderDuration) => {
    const entityOrders = workOrders.filter((o) => o.team === entityId);
    let suggestedDate = new Date();
    while (
      entityOrders.some(
        (o) =>
          suggestedDate >= new Date(o.planned_date_start) &&
          suggestedDate < new Date(o.planned_date_end)
      )
    ) {
      suggestedDate.setHours(suggestedDate.getHours() + 1);
    }
    return suggestedDate;
  };

  const getTeamLoad = (entityId, day) => {
    const dayOrders = workOrders.filter(
      (o) =>
        o.team === entityId &&
        new Date(o.planned_date_start).toDateString() === day.toDateString()
    );
    return dayOrders.reduce(
      (total, o) =>
        total +
        (new Date(o.planned_date_end) - new Date(o.planned_date_start)) /
        (1000 * 60 * 60),
      0
    ).toFixed(1);
  };

  const assignOrdersToTeam = async (teamId) => {
    try {
      const token = localStorage.getItem("access_token");
      for (const orderId of selectedOrders) {
        const order = workOrders.find((o) => o.id === orderId);
        await axios.put(
          `${API_BASE_URL}/workpermit/${orderId}/`,
          { ...order, team: teamId },
          { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
        );
      }
      fetchCompanyData();
      toast.success(`Assigned ${selectedOrders.size} orders to team`);
    } catch (error) {
      console.error("Error assigning orders:", error);
      toast.error("Error assigning orders");
    }
  };

  const exportPlan = () => {
    const csv = workOrders.map((o) => `${o.work_permit_number},${o.team},${o.planned_date_start}`).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "plan.csv";
    a.click();
    toast.success("Plan exported successfully");
  };

  const navigate = useNavigate();

  const handleEditTeam = (team) => {
    navigate("/teammanager", { state: { editingTeam: team || null } });
  };

  return (
    <div className="min-h-screen bg-steel-50">
      <DndProvider backend={HTML5Backend}>
        <div className="min-h-screen bg-white border border-steel-200">
          {/* Main Content */}
          <div className="bg-gradient-to-r from-brand-50 to-steel-50 p-4">
            <div className="flex flex-col space-y-2">
              {/* Title, Calendar Navigation, and Icons */}
              <div className="flex flex-col md:flex-row md:items-center gap-2">
                <div className="ml-4 items-start text-2xl font-bold text-slate-900">
                  Work Permit Panel
                </div>
                <div className="md:flex-1 md:flex md:justify-center">
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigateWeek("prev")}
                      className="shadow-sm h-8 w-8 p-0"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <div className="flex items-center space-x-2">
                      <h2 className="text-xl font-bold text-slate-800">
                        {formatDateRange(currentWeekStart)}
                      </h2>
                      <select
                        className="px-2 py-1 pr-8 text-xs border border-steel-300 rounded-xl shadow-sm bg-white focus:outline-none focus:ring-1 focus:ring-brand-500"
                        value={calendarView}
                        onChange={(e) => setCalendarView(e.target.value)}
                      >
                        <option value="week">Week</option>
                        <option value="biweek">2 Weeks</option>
                        <option value="month">Month</option>
                        <option value="3months">3 Months</option>
                        <option value="6months">6 Months</option>
                      </select>
                      { /*
                <div className="flex space-x-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleZoomIn}
                    disabled={zoomLevel >= 2.0}
                    className="shadow-sm h-6 w-6 p-0"
                  >
                    <ZoomIn className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleZoomOut}
                    disabled={zoomLevel <= 0.5}
                    className="shadow-sm h-6 w-6 p-0"
                  >
                    <ZoomOut className="h-3 w-3" />
                  </Button>
                </div>
                */}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigateWeek("next")}
                      className="shadow-sm h-8 w-8 p-0"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="default"
                    onClick={handleSavePlanning}
                    disabled={loading}
                    className="bg-brand-500 hover:bg-brand-600 text-white text-sm h-8 px-2 rounded-xl shadow-lg shadow-brand-500/20"
                  >
                    <Download className="h-4 w-4 mr-1" />
                    {loading ? "Saving..." : "Save"}
                  </Button>
                  <Button
                    variant="default"
                    onClick={handleUndo}
                    disabled={history.length === 0 || loading}
                    className="bg-red-50 hover:bg-red-200 text-sm h-8 px-2"
                  >
                    <Undo className="h-4 w-4 mr-1" />
                    Undo
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="shadow-sm h-8 w-8 p-0"
                      >
                        <Menu className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="p-1">
                      <DropdownMenuItem
                        onClick={() => setShowOrderModal(true)}
                        className="flex items-center text-sm p-3 cursor-pointer hover:bg-brand-50 text-brand-600"
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Create Order
                        <span className="ml-auto text-slate-300 text-xs">( Ctrl+O )</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => setShowOrdersList(true)}
                        className="flex items-center text-sm p-3 cursor-pointer hover:bg-brand-50 text-brand-600"
                      >
                        <FileText className="h-3 w-3 mr-1" />
                        Orders List
                        <span className="ml-auto text-slate-300 text-xs">( Ctrl+L )</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => navigate("/teammanager")}
                        className="flex items-center text-sm p-3 cursor-pointer hover:bg-brand-50 text-brand-600"
                      >
                        <Users className="h-3 w-3 mr-1" />
                        Manage Teams
                        <span className="ml-auto text-slate-300 text-xs">( Ctrl+M )</span>
                      </DropdownMenuItem>

                      <DropdownMenuItem
                        onClick={() => setShowUploadModal(true)}
                        className="flex items-center text-sm p-3 cursor-pointer hover:bg-brand-50 text-brand-600"
                      >
                        <Upload className="h-3 w-3 mr-1" />
                        Upload Excel
                        <span className="ml-auto text-slate-300 text-xs">( Ctrl+U )</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={exportPlan}
                        className="flex items-center text-sm p-3 cursor-pointer hover:bg-brand-50 text-brand-600"
                      >
                        <Download className="h-3 w-3 mr-1" />
                        Export Plan
                        <span className="ml-auto text-slate-300 text-xs">( Ctrl+P )</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={toggleFullscreen}
                    className="shadow-sm h-8 w-8 p-0"
                  >
                    {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              {/* Status Legend and Search */}
              <div className="flex justify-between items-end">
                <div className="flex items-center text-sm flex-wrap gap-2">
                  <div className="flex items-center text-blue-700">
                    <Circle className="h-3 w-3 mr-1 fill-blue-700" />
                    <span>Planned</span>
                  </div>
                  <div className="flex items-center text-orange-700">
                    <Circle className="h-3 w-3 mr-1 fill-orange-700" />
                    <span>Planned Approved</span>
                  </div>
                  <div className="flex items-center text-yellow-700">
                    <Circle className="h-3 w-3 mr-1 fill-yellow-700" />
                    <span>Signed</span>
                  </div>
                  <div className="flex items-center text-green-700">
                    <Circle className="h-3 w-3 mr-1 fill-green-700" />
                    <span>Approved</span>
                  </div>
                  <div className="flex items-center text-red-700">
                    <Circle className="h-3 w-3 mr-1 fill-red-700" />
                    <span>Rejected</span>
                  </div>
                  <div className="flex items-center text-slate-700">
                    <Circle className="h-3 w-3 mr-1 fill-slate-700" />
                    <span>Closed</span>
                  </div>
                  <div className="flex items-center text-red-700">
                    <Circle className="h-3 w-3 mr-1 fill-red-700" />
                    <span>Duplicate</span>
                  </div>
                  
                </div>
                <div className="w-96 flex items-center bg-white rounded-full border border-steel-300 shadow-sm p-2 focus-within:ring-1 focus-within:ring-brand-500 focus-within:border-brand-500">
                  <Search className="h-5 w-5 text-slate-400 ml-4 mr-2" />
                  <input
                    type="text"
                    placeholder="Search orders by number..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full p-1 text-sm rounded border-none focus:ring-0 focus:outline-none"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Calendar grid */}
          <div className="p-0 border-b">
            <div className="overflow-x-auto">
              <div className="max-h-[calc(100vh-120px)] overflow-y-auto">
                <div
                  className={`inline-grid ${calendarView === "week"
                    ? "min-w-[1000px]"
                    : calendarView === "biweek"
                      ? "min-w-[2000px]"
                      : calendarView === "month"
                        ? "min-w-[4000px]"
                        : calendarView === "3months"
                          ? "min-w-[12000px]"
                          : "min-w-[24000px]"
                    }`}
                  style={{
                    gridTemplateColumns: `250px repeat(${getDaysForView().length}, minmax(100px, 1fr))`,
                    transform: `scale(${zoomLevel})`,
                    transformOrigin: "top left",
                    width: `${100 / zoomLevel}%`,
                  }}
                >
                  {/* Teams/People selector */}
                  <div className="sticky top-0 left-0 z-30 bg-steel-50 p-2 border-b border-r justify-center">
                    <div className="px-2">
                      <div className="">
                        <nav className="-mb-px flex justify-center space-x-8">
                          {['teams', 'people'].map((opt) => (
                            <button
                              key={opt}
                              onClick={() => setViewType(opt)}
                              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${viewType === opt ? 'border-brand-500 text-brand-600' : 'border-transparent text-slate-400 hover:text-slate-500 hover:border-steel-300'}`}
                            >
                              {opt === 'teams' ? 'Teams' : 'People'}
                            </button>
                          ))}
                        </nav>
                      </div>
                      {/*
                      {viewType === "people" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSortDirection(sortDirection === "asc" ? "desc" : "asc")}
                          className="text-sm h-10 px-2 ml-4"
                        >
                          <span className="mr-1">Sort</span>
                          {sortDirection === "asc" ? (
                            <ArrowDownAZ className="h-4 w-4" />
                          ) : (
                            <ArrowDownZA className="h-4 w-4" />
                          )}
                        </Button>
                      )} */}
                    </div>
                  </div>

                  {/* Days header */}
                  {getDaysForView().map((day, index) => (
                    <div
                      key={index}
                      className={`sticky top-0 z-20 bg-steel-50 p-2 border-b ${day.getDay() === 0 ? "bg-red-50" : ""
                        }`}
                    >
                      <div className="text-[10px] font-medium text-slate-700">
                        {day.toLocaleDateString("en-GB", {
                          weekday: "short",
                          day: "numeric",
                          month: "short",
                        })}
                      </div>
                    </div>
                  ))}

                  {/* Entities and calendar cells */}
                  {getActiveEntities().map((entity) => (
                    <React.Fragment key={entity.id}>
                      <div className="sticky left-0 z-10 bg-white p-2 hover:bg-steel-50 border-t border-r">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center space-x-1">
                              {viewType === "people" ? (
                                <button
                                  className="ml-2 font-medium text-xs text-slate-900 truncate cursor-pointer hover:text-brand-600 focus:outline-none"
                                  onDoubleClick={() => {
                                    setEditingUserId(entity.id);
                                    setIsUserFormOpen(true);
                                  }}
                                >
                                  {entity.name}
                                </button>
                              ) : (
                                <>
                                  <span className="ml-2 font-medium text-xs text-slate-900 truncate">
                                    {entity.name}
                                  </span>
                                  {viewType === "teams" && (
                                    <button
                                      className="hover:bg-steel-100 p-1 rounded"
                                      onClick={() => toggleTeamExpansion(entity.id)}
                                    >
                                      {entity.expanded ? (
                                        <ChevronUp className="h-3 w-3 text-slate-500" />
                                      ) : (
                                        <ChevronDown className="h-3 w-3 text-slate-500" />
                                      )}
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                            {viewType === "teams" ? (
                              <div className="text-[10px] text-slate-500 mt-1">
                                <div className="flex items-center space-x-1">
                                  <Users className="h-2 w-2 text-slate-400" />
                                  <span>
                                    {companyUsers
                                      .filter(
                                        (user) =>
                                          user.id === entity.team_leader ||
                                          user.id ===
                                          entity.person_responsible_for_preparation_of_work_place ||
                                          entity.team?.includes(user.id)
                                      )
                                      .map(
                                        (user) =>
                                          `${user.first_name} ${user.last_name}`
                                      )
                                      .join(", ")}
                                  </span>
                                </div>
                              </div>
                            ) : null}
                            {viewType === "teams" && entity.expanded && (
                              <div className="mt-1 pl-2 border-l border-steel-100">
                                <div className="space-y-1">
                                  <div>
                                    <div className="text-[8px] font-medium text-slate-500">Supervisor</div>
                                    <div className="text-[10px]">
                                      {companyUsers.find((u) => u.id === entity.team_leader)?.first_name}{" "}
                                      {companyUsers.find((u) => u.id === entity.team_leader)?.last_name}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-[8px] font-medium text-slate-500">Preparer</div>
                                    <div className="text-[10px]">
                                      {companyUsers.find(
                                        (u) => u.id === entity.person_responsible_for_preparation_of_work_place
                                      )?.first_name}{" "}
                                      {companyUsers.find(
                                        (u) => u.id === entity.person_responsible_for_preparation_of_work_place
                                      )?.last_name}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-[8px] font-medium text-slate-500">Other Team Members</div>
                                    {entity.team?.length > 0 ? (
                                      (() => {
                                        const filteredMembers = entity.team.filter(
                                          (memberId) =>
                                            memberId !== entity.team_leader &&
                                            memberId !== entity.person_responsible_for_preparation_of_work_place
                                        );
                                        return filteredMembers.length > 0 ? (
                                          <div className="space-y-1">
                                            {filteredMembers.map((memberId) => {
                                              const member = companyUsers.find((u) => u.id === memberId);
                                              return (
                                                member && (
                                                  <div key={memberId} className="text-[10px]">
                                                    {member.first_name} {member.last_name}
                                                  </div>
                                                )
                                              );
                                            })}
                                          </div>
                                        ) : (
                                          <div className="text-[10px] text-slate-500">None</div>
                                        );
                                      })()
                                    ) : (
                                      <div className="text-[10px] text-slate-500">None</div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                          {viewType === "teams" && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditTeam(entity)}
                              className="ml-1 flex items-center justify-center"
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                      {getDaysForView().map((day, dayIndex) => (
                        <CalendarCell
                          key={dayIndex}
                          entity={entity}
                          day={day}
                          workOrders={workOrders}
                          onDayClick={handleDayClick}
                          onOrderDoubleClick={handleOrderDoubleClick}
                          fetchCompanyData={fetchCompanyData}
                          selectedOrders={selectedOrders}
                          toggleOrderSelection={toggleOrderSelection}
                          history={history}
                          setHistory={setHistory}
                          getTeamLoad={getTeamLoad}
                          teams={teams}
                          setTeams={setTeams}
                          getTeamForUser={getTeamForUser}
                        />
                      ))}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Modal components */}
          <OrderCreation
            isOpen={showOrderModal}
            onClose={() => setShowOrderModal(false)}
            refreshPreOrders={fetchCompanyData}
          />

          <PreOrderList
            isOpen={showOrdersList}
            onClose={() => setShowOrdersList(false)}
            orders={workOrders}
            teams={teams}
            users={companyUsers}
            onOrdersPlanned={fetchCompanyData}
          />

          {selectedTeamDay && (
            <TeamDayView
              entity={selectedTeamDay.entity}
              date={selectedTeamDay.date}
              viewType={selectedTeamDay.viewType}
              dayOrders={selectedTeamDay.dayOrders}
              onClose={() => setSelectedTeamDay(null)}
              refreshParentData={fetchCompanyData}
            />
          )}

          <TimeEditorModal
            isOpen={showTimeEditor}
            onClose={() => setShowTimeEditor(false)}
            order={selectedOrder}
            onSave={() => {
              setShowTimeEditor(false);
              setSelectedOrder(null);
              fetchCompanyData();
            }}
            workOrders={workOrders}
          />

          <UploadExcelModal
            isOpen={showUploadModal}
            onClose={() => setShowUploadModal(false)}
            onUploadSuccess={fetchCompanyData}
            workOrders={workOrders}
          />
          <AddOrEditUserForm
            isCreatorOpen={isUserFormOpen}
            setIsCreatorOpen={setIsUserFormOpen}
            editingUserId={editingUserId}
            onSave={handleUserSave}
          />

        </div>
      </DndProvider>
    </div>
  );
};

export default WorkPermit;